// src/lib/structured-builder/widget-types/hero-banner.ts
//
// Config type and defaults for the Hero Banner widget.
// 4 variants: carousel, full-bleed, split, editorial.

import type { ButtonVariant, ButtonColor, ButtonSize, ButtonShape } from '@/components/storefront/ui/button';

export type HeroVariant = 'carousel' | 'full-bleed' | 'split' | 'editorial';
export type HeroHeight = 'sm' | 'md' | 'lg' | 'full';
export type HeroTextPosition = 'start' | 'center' | 'end';
export type HeroTextAlign = 'start' | 'center' | 'end';
export type HeroSplitLayout = '50-50' | '60-40' | '40-60';
export type HeroImageFit = 'cover' | 'contain';

/** Desktop is the base; these two are the sizes that may diverge from it. */
export type HeroBreakpoint = 'mobile' | 'tablet' | 'desktop';

/**
 * A breakpoint's overrides. Anything left undefined inherits from the next
 * size up — mobile from tablet, tablet from the desktop base — which is the
 * same rule the builder's layout overrides already follow.
 */
export type HeroResponsiveOverride = {
  variant?: HeroVariant;
  height?: HeroHeight;
  /** Exact height in px. Wins over the `height` preset at the same level. */
  height_px?: number;
  text_position?: HeroTextPosition;
  text_align?: HeroTextAlign;
  overlay_opacity?: number;
};

/** A single slide in the hero banner */
export type HeroSlide = {
  id: string;
  /** Background image URL */
  image: string;
  /** Heading */
  heading_ar: string;
  heading_en: string;
  /** Subtitle */
  subtitle_ar: string;
  subtitle_en: string;
  /** CTA button */
  cta_label_ar: string;
  cta_label_en: string;
  cta_url: string;
  cta_new_tab: boolean;
  /** CTA button style */
  cta_variant: ButtonVariant;
  cta_color: ButtonColor;
  cta_size: ButtonSize;
  cta_shape: ButtonShape;
  /** Secondary CTA (optional) */
  cta2_label_ar?: string;
  cta2_label_en?: string;
  cta2_url?: string;
  cta2_variant?: ButtonVariant;
  cta2_color?: ButtonColor;
  /** Editorial: small line above the heading */
  eyebrow_ar?: string;
  eyebrow_en?: string;
  /** Editorial: second, smaller photo layered over the first */
  image2?: string;
  /** Editorial: label pinned to the main photo, linking to `chip_url` */
  chip_label_ar?: string;
  chip_label_en?: string;
  chip_url?: string;
};

export type HeroBannerConfig = {
  _type: 'hero_banner';
  /** Visual variant */
  variant: HeroVariant;
  /** Slides */
  slides: HeroSlide[];
  /** Height preset */
  height: HeroHeight;
  /** Text overlay position (vertical) */
  text_position: HeroTextPosition;
  /** Text alignment */
  text_align: HeroTextAlign;
  /** Overlay darkness (0-100) */
  overlay_opacity: number;
  /** Overlay color */
  overlay_color: string;
  /** Heading text color */
  text_color: string;
  /** Subtitle text color */
  subtitle_color: string;
  /** Carousel: autoplay interval in seconds (0 = disabled) */
  autoplay: number;
  /** Carousel: show dots */
  show_dots: boolean;
  /** Carousel: show arrows */
  show_arrows: boolean;
  /** Split: layout ratio */
  split_layout: HeroSplitLayout;
  /** Split: image side */
  split_image_side: 'start' | 'end';
  /** Split: background color for text side */
  split_bg: string;
  /**
   * How the slide image fills its box. `cover` crops to fill (the original
   * behaviour); `contain` shows the whole image, letterboxed.
   */
  image_fit?: HeroImageFit;
  /** Exact desktop height in px. Wins over the `height` preset when set. */
  height_px?: number;
  /**
   * Heading tag for the active slide's heading. Absent/'h2' keeps the
   * long-standing behaviour (this widget usually sits below a page's real
   * lead element). Set to 'h1' only when this hero IS the page's lead
   * element — e.g. a brand template with no other heading above it.
   */
  heading_level?: 'h1' | 'h2';
  /** Per-breakpoint overrides. Absent on documents saved before this existed. */
  responsive?: {
    tablet?: HeroResponsiveOverride;
    mobile?: HeroResponsiveOverride;
  };
};

function generateSlideId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function createEmptySlide(): HeroSlide {
  return {
    id: generateSlideId(),
    image: '',
    heading_ar: 'عنوان رئيسي',
    heading_en: 'Main Heading',
    subtitle_ar: 'نص فرعي يصف العرض أو المنتج',
    subtitle_en: 'Subtitle describing the offer or product',
    cta_label_ar: 'تسوق الآن',
    cta_label_en: 'Shop Now',
    cta_url: '',
    cta_new_tab: false,
    cta_variant: 'solid',
    cta_color: 'primary',
    cta_size: 'lg',
    cta_shape: 'rounded',
  };
}

export const DEFAULT_HERO_BANNER_CONFIG: HeroBannerConfig = {
  _type: 'hero_banner',
  variant: 'full-bleed',
  slides: [createEmptySlide()],
  height: 'lg',
  text_position: 'center',
  text_align: 'center',
  overlay_opacity: 40,
  overlay_color: '#000000',
  text_color: '#ffffff',
  subtitle_color: '#e5e7eb',
  autoplay: 5,
  show_dots: true,
  show_arrows: true,
  split_layout: '50-50',
  split_image_side: 'end',
  split_bg: '#f9fafb',
  image_fit: 'cover',
};

export const HERO_VARIANTS: { key: HeroVariant; label: string; desc: string }[] = [
  { key: 'carousel', label: 'Carousel', desc: 'Multiple slides with navigation' },
  { key: 'full-bleed', label: 'Full Bleed', desc: 'Full-width image with text overlay' },
  { key: 'split', label: 'Split', desc: 'Image + text side by side' },
  { key: 'editorial', label: 'Editorial', desc: 'Text beside two layered photos' },
];

/**
 * `narrow` applies below 768px, `wide` from 768px up — the two bands the old
 * `min-h-[300px] md:min-h-[360px]` classes encoded. Keeping both means a hero
 * with no responsive overrides renders at exactly the heights it always did.
 */
export const HERO_HEIGHTS: {
  key: HeroHeight;
  label: string;
  cls: string;
  narrow: string;
  wide: string;
}[] = [
  { key: 'sm', label: 'Small', cls: 'min-h-[300px] md:min-h-[360px]', narrow: '300px', wide: '360px' },
  { key: 'md', label: 'Medium', cls: 'min-h-[400px] md:min-h-[480px]', narrow: '400px', wide: '480px' },
  { key: 'lg', label: 'Large', cls: 'min-h-[500px] md:min-h-[600px]', narrow: '500px', wide: '600px' },
  { key: 'full', label: 'Full Screen', cls: 'min-h-[100dvh]', narrow: '100dvh', wide: '100dvh' },
];

// ── Per-breakpoint resolution ──────────────────────────────────────

export type ResolvedHero = {
  variant: HeroVariant;
  /** A CSS length, ready for `min-height`. */
  height: string;
  textPosition: HeroTextPosition;
  textAlign: HeroTextAlign;
  /** 0-100, as authored. */
  overlayOpacity: number;
  /** True when `height` came from an exact px value rather than a preset. */
  heightIsCustom: boolean;
};

/**
 * The `quality` and `sizes` each variant renders its first slide's image with.
 * HeroBannerClient renders with these and [locale]/layout.tsx preloads with
 * them. They must agree: a preload for a different quality or candidate set is
 * a different URL, so the LCP image would download twice.
 */
export const HERO_LEAD_IMAGE: Record<HeroVariant, { quality: number; sizes: string }> = {
  carousel: { quality: 80, sizes: '100vw' },
  'full-bleed': { quality: 85, sizes: '100vw' },
  split: { quality: 85, sizes: '100vw' },
  editorial: { quality: 85, sizes: '(min-width: 1280px) 420px, (min-width: 768px) 36vw, 80vw' },
};

/**
 * Collapse the base config and the override chain into the values that apply
 * at one breakpoint.
 *
 * `height` and `height_px` are mutually exclusive at each level: a breakpoint
 * that picks a preset drops an inherited pixel height and vice versa, so a
 * tablet override of "Medium" isn't silently ignored because desktop set an
 * exact px value.
 */
export function resolveHeroAt(
  config: HeroBannerConfig,
  breakpoint: HeroBreakpoint,
): ResolvedHero {
  const chain: (HeroResponsiveOverride | undefined)[] =
    breakpoint === 'desktop'
      ? []
      : breakpoint === 'tablet'
        ? [config.responsive?.tablet]
        : [config.responsive?.tablet, config.responsive?.mobile];

  const merged: HeroResponsiveOverride = {
    variant: config.variant,
    height: config.height,
    height_px: config.height_px,
    text_position: config.text_position,
    text_align: config.text_align,
    overlay_opacity: config.overlay_opacity,
  };

  for (const layer of chain) {
    if (!layer) continue;
    if (layer.variant !== undefined) merged.variant = layer.variant;
    if (layer.text_position !== undefined) merged.text_position = layer.text_position;
    if (layer.text_align !== undefined) merged.text_align = layer.text_align;
    if (layer.overlay_opacity !== undefined) merged.overlay_opacity = layer.overlay_opacity;
    if (layer.height_px !== undefined) {
      merged.height_px = layer.height_px;
      merged.height = undefined;
    } else if (layer.height !== undefined) {
      merged.height = layer.height;
      merged.height_px = undefined;
    }
  }

  const preset =
    HERO_HEIGHTS.find((h) => h.key === merged.height) ?? HERO_HEIGHTS[2];
  const heightIsCustom = merged.height_px != null && merged.height_px > 0;
  const height = heightIsCustom
    ? `${merged.height_px}px`
    : breakpoint === 'mobile'
      ? preset.narrow
      : preset.wide;

  return {
    variant: merged.variant ?? config.variant,
    height,
    heightIsCustom,
    textPosition: merged.text_position ?? config.text_position,
    textAlign: merged.text_align ?? config.text_align,
    overlayOpacity: merged.overlay_opacity ?? config.overlay_opacity,
  };
}

/** Vertical padding that pairs with a top/bottom-anchored text block. */
const POSITION_PADDING: Record<HeroBreakpoint, string> = {
  mobile: '4rem',
  tablet: '6rem',
  desktop: '6rem',
};

const JUSTIFY: Record<HeroTextPosition, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
};

const ITEMS: Record<HeroTextAlign, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
};

/**
 * Every breakpoint's resolved values, as the CSS custom properties the
 * `.sf-hero*` rules in globals.css read. Resolving all three up front means
 * the breakpoint switch is pure CSS — no media-query hook, no hydration flash
 * on the page's LCP element.
 */
export function heroCSSVars(config: HeroBannerConfig): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const bp of ['desktop', 'tablet', 'mobile'] as const) {
    const r = resolveHeroAt(config, bp);
    vars[`--hero-h-${bp}`] = r.height;
    vars[`--hero-overlay-${bp}`] = String(r.overlayOpacity / 100);
    vars[`--hero-justify-${bp}`] = JUSTIFY[r.textPosition];
    vars[`--hero-items-${bp}`] = ITEMS[r.textAlign];
    vars[`--hero-text-align-${bp}`] = r.textAlign;
    vars[`--hero-pt-${bp}`] =
      r.textPosition === 'start' ? POSITION_PADDING[bp] : '0px';
    vars[`--hero-pb-${bp}`] =
      r.textPosition === 'end' ? POSITION_PADDING[bp] : '0px';
  }
  return vars;
}

/**
 * Tailwind visibility for the set of breakpoints a variant covers. Returns ''
 * when it covers all three — the common case, where one DOM tree is rendered
 * and nothing is duplicated.
 */
export function heroVisibilityClass(breakpoints: HeroBreakpoint[]): string {
  const key = ['mobile', 'tablet', 'desktop']
    .filter((b) => breakpoints.includes(b as HeroBreakpoint))
    .join('+');
  const MAP: Record<string, string> = {
    'mobile+tablet+desktop': '',
    mobile: 'md:hidden',
    tablet: 'hidden md:block lg:hidden',
    desktop: 'hidden lg:block',
    'mobile+tablet': 'lg:hidden',
    'tablet+desktop': 'hidden md:block',
    'mobile+desktop': 'md:max-lg:hidden',
  };
  return MAP[key] ?? '';
}

/**
 * Heading and subtitle colours for the split variant's text panel. The panel
 * ignores text_color — that is tuned for text over a photo — so the colours
 * follow the panel instead: dark on a light panel (the long-standing default),
 * white on a dark one. Anything that isn't a hex colour keeps the dark pair.
 */
export function splitPanelTextColors(bg: string | undefined): { text: string; subtitle: string } {
  const dark = { text: '#111827', subtitle: '#6b7280' };
  const hex = bg?.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1];
  if (!hex) return dark;
  const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex;
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(full.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  // Crossover where white and #111827 have equal contrast against the panel.
  return luminance < 0.18 ? { text: '#ffffff', subtitle: 'rgba(255, 255, 255, 0.8)' } : dark;
}
